import React, { useCallback, useEffect, useContext, useState, useMemo, CSSProperties } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { MTableToolbar, Query } from '@material-table/core';
import MTable from '../../../../../Components/MTable/MTable';
import { HtmlView } from '../../../../../Components/Html/Html.View';
import { OwnNotes } from '../../../../../Components/OwnNotes';
import { useFilter } from '../../../../../Components/ListFilter';
import { RequirementListFilter } from './Filter';
import { ModuleContext } from '../../../../../Context/ModuleContext/ModuleContext';
import { IGridQueryRequest, INamedEntity, OrderDirection } from '../../../../../Api/AptorApi';
import { useAptorApi } from '../../../../../Api';
import { useIsMounted } from '../../../../../Hooks/useIsMounted';
import { useStyles } from '../../../LawPortal.styles';
import moment from 'moment';
import { RepealableName } from '../../../../../Components';
import { AccessLevel, LawPortalFeature, UserContext } from '../../../../../Context/UserContext/UserContext';
import { Button, Tooltip } from '@material-ui/core';
import { ArrowUpward, SaveAlt } from '@material-ui/icons';
import LatestComplianceControlDialog from "./LatestComplianceControlDialog";
import { useSnackbar } from 'notistack';

type RequirementData = {
  id: number;
  name: string;
  description: string;
  area: string;
  effectiveTo?: Date;
  changedDate?: Date;
  companyUnits: Array<INamedEntity & { note: string | null }>;
};

export const RequirementList = () => {
  const { formatMessage } = useIntl();
  const intl = useIntl();
  const { api } = useAptorApi();
  const [filters, setFilter] = useFilter();
  const tableRef = React.useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = React.useState(false);
  const queryParams = new URLSearchParams(location.search);
  const isMounted = useIsMounted();
  const { setHeader } = useContext(ModuleContext);
  const user = useContext(UserContext);
  const isManager = user.hasAccessToFeature(LawPortalFeature.Manage, AccessLevel.Manage);
  const classes = useStyles();
  const [reqIdForCompliance, setReqIdForCompliance] = useState("");
  const [isFiltersInitialized, setIsFiltersInitialized] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  // ✅ FIX: Separate hidden state from column definitions
  const [hiddenFields, setHiddenFields] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setHeader(formatMessage({ id: 'law-portal.requirements.header', defaultMessage: 'Other requirements' }), [
      {
        title: formatMessage({ id: 'law-portal.requirements.header', defaultMessage: 'Other requirements' }),
        to: `/law-portal/other-requirements`,
      },
      {
        title: formatMessage({ id: 'law-portal.statistics.sub-menu.changes.unacknowledged' }),
        to: `/law-portal/other-requirements/unacknowledged`,
      },
      {
        title: formatMessage({ id: 'law-portal.statistics.sub-menu.changes.acknowledged' }),
        to: `/law-portal/other-requirements/acknowledged`,
      },
    ]);
  }, [setHeader, formatMessage]);

  useEffect(() => {
    if (isMounted && isFiltersInitialized) {
      tableRef.current && (tableRef.current as any).onQueryChange();
    }
  }, [filters, tableRef, isMounted, isFiltersInitialized]);

  useEffect(() => {
    const queryParamsMap = {
      status: 'status',
      companyUnit: 'companyUnit',
      complianceUsers: 'complianceUsers',
      processes: 'processes',
      aspects: 'aspects',
      area: 'area',
      tags: 'tags',
    };
    Object.entries(queryParamsMap).forEach(([queryKey, stateKey]) => {
      const queryValues = queryParams.getAll(queryKey);
      if (queryValues.length) {
        setFilter(stateKey, queryValues.join(',').split(',').map(num => num.toString()));
      }
    });
    setIsFiltersInitialized(true);
  }, []);

  const handleDataFetch = useCallback(
    async (query: Query<RequirementData>) => {
      if (!isFiltersInitialized) {
        return { data: [], page: 0, totalCount: 0 };
      }
      return await api.searchLawPortalRequirements<RequirementData>({
        orderDirection: query.orderDirection === 'desc' ? OrderDirection.Descending : OrderDirection.Ascending,
        orderBy: query.orderBy !== undefined ? query.orderBy.field : 'name',
        page: query.page,
        pageSize: query.pageSize,
        search: query.search,
        filters: filters,
      } as IGridQueryRequest);
    },
    [api, filters, isFiltersInitialized],
  );

  const renderDate = (date?: Date) => {
    return date ? moment(date).format('ll') : undefined;
  };

  const [showScrollTop, setShowScrollTop] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const updatedQueryParams = Object.keys(filters)
      .filter(key => filters[key]?.join() !== undefined && filters[key]?.join() !== "")
      .map(key => `${key}=${encodeURIComponent(filters[key]?.join())}`)
      .join('&');
    navigate({
      pathname: '',
      search: `?${updatedQueryParams}`,
    });
  }, [filters, navigate]);

  const handleExport = async () => {
    const response = await api.exportLawPortalRequirements();
    const blob = await response?.blob();
    const contentDisposition = response.headers.get('Content-Disposition');
    let fileName = 'RequirementExport.xlsx';
    if (contentDisposition && contentDisposition.includes('attachment')) {
      const matches = /filename=([^;]*)/.exec(contentDisposition);
      if (matches && matches[1]) {
        fileName = matches[1];
      }
    }
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
  };

  // ✅ FIX: useMemo re-runs whenever formatMessage changes (i.e., on language switch)
  // Previously useState only ran once at mount, so titles were stale after language change
  const baseColumns = useMemo(() => [
    {
      title: formatMessage({ id: 'law-portal.requirements.table.name' }),
      field: 'name',
      render: (row: any) => <RepealableName name={row.name} effectiveTo={row.effectiveTo} />,
      hidden: false,
    },
    {
      title: formatMessage({ id: 'law-portal.requirements.table.description' }),
      field: 'description',
      render: (row: any) => <HtmlView variant="body2" value={row.description} />,
      sorting: false,
      hidden: false,
    },
    {
      title: formatMessage({ id: 'law-portal.requirements.table.area' }),
      field: 'area',
      hidden: false,
    },
    {
      title: formatMessage({ id: 'law-portal.requirements.table.changedDate' }),
      field: 'changedDate',
      render: (row: any) => renderDate(row.changedDate),
      hidden: false,
    },
    {
      title: formatMessage({ id: 'law-portal.requirements.table.notes' }),
      field: 'notes',
      render: (row: any) => <OwnNotes {...row} />,
      sorting: false,
      hidden: false,
    },
    {
      title: formatMessage({ id: 'law-portal.requirements.table.latest-compliance-control' }),
      field: 'compilancelog',
      render: (row: any) => (
        <Button
          onClick={(event) => {
            event.stopPropagation();
            setReqIdForCompliance(row.id);
            setOpen(true);
          }}
          disabled={row?.logCount == null || row?.logCount == 0}
        >
          {formatMessage({ id: "law-portal.our-laws.table-row.latest-compliance-control.req.button.name", defaultMessage: "Compliance Log" })}
        </Button>
      ),
      sorting: false,
      hidden: false,
    },
    {
      title: formatMessage({ id: 'law-portal.compliance.report.table.col' }),
      field: 'responsible',
      render: (row: any) => {
        if (row.responsible) {
          const responsiblearr = row?.responsible?.split(',');
          return (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {responsiblearr?.map((val: any, i: number) => <span key={i}>{val}</span>)}
            </div>
          );
        }
        return "-";
      },
      sorting: false,
      hidden: false,
    },
    {
      title: formatMessage({ id: 'utils.filters.processes' }),
      field: 'processes',
      render: (row: any) => {
        if (row.processes) {
          const processarr = row?.processes?.split(',');
          return (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {processarr?.map((val: any, i: number) => <span key={i}>{val}</span>)}
            </div>
          );
        }
        return "-";
      },
      sorting: false,
      hidden: false,
    },
    {
      title: formatMessage({ id: 'utils.filters.aspects' }),
      field: 'aspects',
      render: (row: any) => {
        if (row.aspects) {
          const aspectsarr = row?.aspects?.split(',');
          return (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {aspectsarr?.map((val: any, i: number) => <span key={i}>{val}</span>)}
            </div>
          );
        }
        return "-";
      },
      sorting: false,
      hidden: false,
    },
    {
      title: formatMessage({ id: 'utils.filters.tags' }),
      field: 'tags',
      render: (row: any) => {
        if (row.tags) {
          const tagsarr = row?.tags?.split(',');
          return (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {tagsarr?.map((val: any, i: number) => <span key={i}>{val}</span>)}
            </div>
          );
        }
        return "-";
      },
      sorting: false,
      hidden: false,
    },
    {
      title: (
        <Button className='custom-save-button' onClick={() => handleSubmit()}>
          {formatMessage({ id: 'form.save' })}
        </Button>
      ),
      field: 'button',
      hidden: true,
    },
  ], [formatMessage]); // ✅ re-runs on language change

  // ✅ FIX: Merge base column definitions with user's hidden preferences
  const tableColumn = useMemo(() =>
    baseColumns.map(col => ({
      ...col,
      hidden: col.field in hiddenFields ? hiddenFields[col.field] : col.hidden,
    })),
    [baseColumns, hiddenFields]
  );

  const getColumn = async (id: any, lawName: any) => {
    const item = await api.getSaveColumn(id, lawName);
    if (item?.length) {
      const newHidden: Record<string, boolean> = {};
      baseColumns.forEach((col: any) => {
        if (col.field !== 'button') {
          newHidden[col.field] = !item.includes(col.field);
        }
      });
      setHiddenFields(newHidden);
    }
  };

  useEffect(() => {
    const data: any = localStorage.getItem('user');
    const user = JSON.parse(data);
    if (user) {
      getColumn(user?.id, 2);
    }
  }, []);


  const handleSubmit=()=>{
    let checkItem:any=[]
    tableColumn.forEach((val:any)=>{
      if(val?.hidden==false&&val?.field!='button'){
        checkItem.push(val?.field)
      }
    })
    let payload={
    TableValue: 2,
    Columns: [...checkItem]
  }
  if(checkItem?.length){
    api.saveColumn(payload)
  }else{
    enqueueSnackbar(intl.formatMessage({ id:'law-portal.minimum_one_select_checkbox' }), {
      variant: 'error',
    });
  } 
}

  const tableOptions = useMemo(() => ({
    rowStyle: (row: RequirementData, index: number, level: number): CSSProperties => {
      const disabled = !(isManager || user.complianceUserFor.requirements.some(x => x.id === row.id));
      return disabled ? { pointerEvents: 'none' as const } : {};
    },
  }), [isManager, user.complianceUserFor.requirements]);

  return (
    <div className={classes.listWrapper}>
      <RequirementListFilter filters={filters} setFilter={setFilter} />
      <hr style={{ marginTop: '25px', marginBottom: "25px" }} />
      <MTable
        tableRef={tableRef}
        columns={tableColumn}
        data={handleDataFetch}
        onRowClick={(_, row) =>
          row &&
          (isManager || user.complianceUserFor.requirements.some((x) => x.id === row.id)) &&
          navigate(`/law-portal/other-requirements/${row.id}`)
        }
        options={tableOptions}
        components={{
          Toolbar: (props) => (
            <div className={classes.toolbar}>
              <MTableToolbar {...props} />
              <SaveAlt style={{ cursor: 'pointer' }} onClick={() => handleExport()} />
            </div>
          ),
        }}
      />
      {open && reqIdForCompliance !== "" && (
        <LatestComplianceControlDialog
          open={open}
          reqId={reqIdForCompliance}
          setOpen={(val: boolean) => {
            setOpen(val);
            setReqIdForCompliance("");
          }}
        />
      )}
      {showScrollTop && (
        <Tooltip title={formatMessage({ id: "bottom.to.top.btn.tooltip.text", defaultMessage: "Back to top" })}>
          <Button
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className={classes.topButton}
          >
            <ArrowUpward style={{ fontSize: 20 }} />
          </Button>
        </Tooltip>
      )}
    </div>
  );
};